export const DEFAULT_AGENT_LOCATION_REPORTING_API = 'https://maxpark-firebase-sync-worker.onrender.com';

import {
  getReportingProxyHeaders,
  getReportingProxyUrl,
} from '../../../utils/reportingClient';

const AGENT_LOCATIONS_CACHE_MS = 30000;
const DEFAULT_AGENT_LOCATIONS_SINCE_HOURS = 24;
const FIREBASE_UID_PATTERN = /^[A-Za-z0-9_-]{20,40}$/;

let agentLocationsCache = null;

const cleanUrl = value => String(value || '').trim().replace(/\/+$/, '');
const cleanString = value => String(value || '').trim();

export const isAgentUidLike = value => {
  const stringValue = cleanString(value);
  return Boolean(stringValue) && FIREBASE_UID_PATTERN.test(stringValue) && !stringValue.includes('@');
};

const isDisplayableAgentIdentity = value => Boolean(cleanString(value)) && !isAgentUidLike(value);

const pickString = (source, keys) => {
  for (const key of keys) {
    const value = cleanString(source[key]);
    if (value) {
      return value;
    }
  }

  return '';
};

const getReportingApiOrigin = configuredOrigin => cleanUrl(configuredOrigin);

const normalizeNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCount = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getAgentLocationsReportUrl = (options = {}) => {
  const origin = getReportingApiOrigin(options.reportingApi);
  const params = new URLSearchParams();

  params.set('sinceHours', String(options.sinceHours || DEFAULT_AGENT_LOCATIONS_SINCE_HOURS));
  params.set('limit', String(options.limit || 250));

  if (options.now) {
    const now = options.now instanceof Date ? options.now : new Date(options.now);
    if (!Number.isNaN(now.getTime())) {
      params.set('now', now.toISOString());
    }
  }

  return origin
    ? `${origin}/reports/agent-locations?${params.toString()}`
    : getReportingProxyUrl('agent-locations', params);
};

const normalizeAgentLocation = (agent = {}, index = 0) => {
  const agentKey = pickString(agent, ['agentKey', 'agent_key', 'agentUID', 'agentUid']);
  const agentId = pickString(agent, ['agentId', 'agent_id', 'agentUID', 'agentUid', 'agentid', 'uid', 'userId']);
  const agentEmail = pickString(agent, ['agentEmail', 'agent_email', 'email', 'userEmail']).toLowerCase();
  const agentNameCandidate = pickString(agent, [
    'agentName',
    'agent_name',
    'displayName',
    'name',
    'userName',
    'agent',
  ]);
  const fallbackIdentity = [
    agentEmail,
    agentId,
    agentKey,
  ].find(isDisplayableAgentIdentity);
  const agentName = isDisplayableAgentIdentity(agentNameCandidate)
    ? agentNameCandidate
    : fallbackIdentity || 'Agent MaxPark';
  const eventType = pickString(agent, ['eventType', 'event_type']);
  const eventLabel = pickString(agent, ['eventLabel', 'event_label']) || eventType;
  const rawAgeMinutes = agent.ageMinutes === undefined ? agent.age_minutes : agent.ageMinutes;

  return {
    id: pickString(agent, ['eventId', 'event_id', 'id']) || `${agentKey || agentId || agentEmail || 'agent'}-${index}`,
    agentKey,
    agentId,
    agentEmail,
    agentName,
    eventType,
    eventLabel,
    ticketId: pickString(agent, ['ticketId', 'ticket_id', 'noticeId', 'notice_id']),
    latitude: normalizeNumber(agent.latitude || agent.lat),
    longitude: normalizeNumber(agent.longitude || agent.lng || agent.lon),
    accuracyMeters: normalizeNumber(agent.accuracyMeters || agent.accuracy_meters),
    occurredAt: pickString(agent, ['occurredAt', 'occurred_at']),
    ageMinutes: rawAgeMinutes === null || rawAgeMinutes === undefined
      ? null
      : normalizeCount(rawAgeMinutes),
  };
};

const normalizeAgentLocationsReport = (report = {}) => {
  const agents = Array.isArray(report.agents)
    ? report.agents.map(normalizeAgentLocation).filter(agent =>
      agent.latitude !== null && agent.longitude !== null
    )
    : [];

  return {
    agents,
    totalAgents: normalizeCount(report.totalAgents || agents.length),
    returnedAgents: normalizeCount(report.returnedAgents || agents.length),
    sinceHours: normalizeCount(report.sinceHours || DEFAULT_AGENT_LOCATIONS_SINCE_HOURS),
    loadedSources: Array.isArray(report.loadedSources) && report.loadedSources.length
      ? report.loadedSources
      : ['render-postgres'],
    dataSource: report.dataSource || 'render-postgres',
    loadedAt: report.loadedAt || new Date().toISOString(),
  };
};

export const loadAgentLocationsData = async (options = {}) => {
  const cacheKey = [
    getReportingApiOrigin(options.reportingApi),
    options.sinceHours || DEFAULT_AGENT_LOCATIONS_SINCE_HOURS,
    options.limit || 250,
  ].join(':');

  if (
    !options.skipCache
    && agentLocationsCache
    && agentLocationsCache.key === cacheKey
    && Date.now() - agentLocationsCache.cachedAt < AGENT_LOCATIONS_CACHE_MS
  ) {
    return agentLocationsCache.data;
  }

  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);

  if (!fetchImpl) {
    throw new Error('Impossible de charger les positions agents: fetch non disponible.');
  }

  const headers = options.reportingApi
    ? { Accept: 'application/json' }
    : await getReportingProxyHeaders();
  const response = await fetchImpl(getAgentLocationsReportUrl(options), {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les positions agents (${response.status}).`);
  }

  const data = normalizeAgentLocationsReport(await response.json());

  agentLocationsCache = {
    key: cacheKey,
    cachedAt: Date.now(),
    data,
  };

  return data;
};
